import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import SEO from "../../components/SEO";
import config from "../../Config";
import { ROUTES } from "../../routes";
import { safeStartViewTransition } from "../../utils/viewTransition";
import "./login.css";

export default function SmsVerifyPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const intervalRef = useRef(null);

    const { phoneNumber, authCode, targetSmsNumber, message, smsText, flow } = location.state || {};

    const [status, setStatus] = useState("pending"); // 'pending' | 'success' | 'error'
    const [statusMessage, setStatusMessage] = useState("Ожидаем подтверждения по SMS…");
    const [errorMessage, setErrorMessage] = useState("");
    const [checkCount, setCheckCount] = useState(0);
    const [copied, setCopied] = useState(false);
    // checkCount kept for internal tracking but not displayed in UI

    const getSmsHref = () => {
        if (!targetSmsNumber) return "#";
        const body = smsText || authCode || "";
        return `sms:${targetSmsNumber}?body=${encodeURIComponent(body)}`;
    };

    const copyToClipboard = async () => {
        if (!smsText) return;
        try {
            await navigator.clipboard.writeText(smsText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = smsText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Redirect if no state (e.g. page refresh)
    useEffect(() => {
        if (!phoneNumber || !authCode) {
            navigate(ROUTES.AUTH, { viewTransition: true });
        }
    }, [phoneNumber, authCode, navigate]);

    // Polling logic
    useEffect(() => {
        if (!phoneNumber || !authCode) return;

        let isMounted = true;

        const checkStatus = async () => {
            if (!isMounted) return;

            try {
                let response;

                if (flow === "change") {
                    const token = Cookies.get(config.authCookie);
                    response = await axios.get("/client/check_change_phone_number", {
                        params: {
                            new_phone_number: phoneNumber,
                            auth_code: authCode,
                        },
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                } else {
                    response = await axios.get("/general/check_sms_authentication", {
                        params: {
                            phone_number: phoneNumber,
                            auth_code: authCode,
                        },
                    });
                }

                if (!isMounted) return;

                const data = response.data;

                // Error object returned with 200
                if (data?.has_error) {
                    if (data.error_code === "AUTH_PENDING") {
                        setStatus("pending");
                        setStatusMessage(data.error_description || "Ожидаем подтверждения по SMS…");
                        setCheckCount((c) => c + 1);
                        return;
                    }

                    if (data.error_code === "AUTH_NOT_COMPLETED") {
                        setStatus("error");
                        setErrorMessage(data.error_description || "Код не найден или просрочен.");
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                        return;
                    }

                    // Unknown error_code
                    setStatus("error");
                    setErrorMessage(data.error_description || "Произошла ошибка.");
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return;
                }

                // Success
                if (flow === "login" && data?.value) {
                    // Save token
                    Cookies.set(config.authCookie, data.value, {
                        expires: 30 * 6,
                    });
                    // Notify contexts
                    window.dispatchEvent(new CustomEvent("user:login"));

                    setStatus("success");
                    setStatusMessage("Вход выполнен! Перенаправляем…");

                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    safeStartViewTransition(() => {
                        navigate(ROUTES.PROFILE, { viewTransition: true });
                    });
                    return;
                }

                if (flow === "change" && data?.id) {
                    // Phone changed successfully
                    window.dispatchEvent(new CustomEvent("user:login"));

                    setStatus("success");
                    setStatusMessage("Номер телефона обновлён! Перенаправляем…");

                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    safeStartViewTransition(() => {
                        navigate(ROUTES.PROFILE, { viewTransition: true });
                    });
                    return;
                }

                // Fallback: treat as pending if no recognizable shape
                setStatus("pending");
                setCheckCount((c) => c + 1);
            } catch (err) {
                if (!isMounted) return;
                // Network or server error — keep polling, but show brief hint after several failures
                setCheckCount((c) => c + 1);
            }
        };

        // Immediate first check
        checkStatus();

        intervalRef.current = setInterval(checkStatus, 3000);

        return () => {
            isMounted = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [phoneNumber, authCode, flow, navigate]);

    const handleBack = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (flow === "change") {
            navigate(ROUTES.PROFILE, { viewTransition: true });
        } else {
            navigate(ROUTES.AUTH, { viewTransition: true });
        }
    };

    const title = flow === "change" ? "Подтвердите смену номера" : "Подтвердите вход по SMS";
    const subtitle = flow === "change"
        ? "Отправьте SMS для подтверждения смены"
        : "Отправьте SMS для входа в аккаунт";

    return (
        <>
            <SEO title={title} description={subtitle} noindex={true} />
            <div className="auth-page">
                <div className="auth-container">
                    <div className="auth-header">
                        <div className="auth-logo">
                            <svg
                                version="1.0"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 294 295"
                                preserveAspectRatio="xMidYMid slice"
                                fill="currentColor"
                            >
                                <g transform="translate(0.000000,295.000000) scale(0.100000,-0.100000)"
                                   fill="#2f6fed"
                                   stroke="none">
                                    <path d="M1281 2855 c-215 -47 -403 -162 -535 -327 -294 -367 -266 -893 63
-1227 166 -168 402 -271 620 -271 l65 0 -39 -41 c-213 -220 -572 -258 -836
-87 -191 122 -309 340 -309 568 0 41 5 95 11 120 8 35 6 63 -7 130 -11 54 -18
135 -18 222 -1 131 -2 136 -19 120 -31 -27 -105 -145 -137 -217 -60 -133 -75
-213 -74 -385 1 -138 4 -165 27 -243 48 -155 117 -276 221 -385 333 -349 866
-385 1246 -85 196 155 314 377 344 648 l11 100 58 -70 c202 -244 221 -557 50
-817 -62 -94 -150 -171 -258 -224 -138 -68 -282 -90 -418 -63 -46 10 -70 8
-144 -7 -58 -12 -134 -18 -220 -18 -73 -1 -133 -2 -133 -3 0 -11 146 -106 215
-139 145 -71 223 -87 405 -87 140 0 164 2 243 26 167 51 303 134 418 253 110
116 177 232 225 389 25 82 27 104 27 245 0 182 -16 260 -87 405 -95 198 -248
347 -450 441 -92 42 -176 63 -306 78 l-105 12 70 57 c145 120 301 173 480 164
178 -10 304 -66 436 -197 149 -147 208 -302 195 -514 -4 -61 -8 -122 -8 -136
-1 -14 7 -71 16 -128 12 -67 17 -138 14 -206 -4 -122 -4 -122 77 -2 65 96 117
221 141 336 24 122 16 314 -20 433 -100 335 -365 578 -706 649 -131 28 -307
21 -433 -16 -384 -113 -651 -448 -669 -838 -2 -38 -5 -68 -8 -68 -13 0 -90 89
-122 140 -234 373 -62 852 359 1001 72 26 269 37 337 20 36 -9 58 -8 113 6 47
11 116 16 220 17 84 1 153 3 153 6 0 9 -110 85 -177 122 -72 40 -182 79 -271
97 -73 15 -274 12 -351 -4z m290 -1230 c64 -42 99 -132 79 -204 -50 -187 -317
-189 -365 -3 -24 92 13 174 98 217 64 33 130 29 188 -10z"/>
                                </g>
                            </svg>
                        </div>
                        <h1 className="auth-title">{title}</h1>
                        <p className="auth-subtitle">{subtitle}</p>
                    </div>

                    <div className="auth-card">
                        {/* Instruction block */}
                        <div className="auth-info" role="status">
                            <div className="auth-info-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                            </div>
                            <p className="auth-info-text">{message || "Отправьте SMS с кодом на указанный номер."}</p>
                        </div>

                        {/* Quick actions */}
                        <div className="sms-quick-actions">
                            <button
                                type="button"
                                className={`sms-action-btn ${copied ? 'sms-action-copied' : ''}`}
                                onClick={copyToClipboard}
                                title="Нажмите, чтобы скопировать"
                            >
                                {copied ? (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Скопировано
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                        </svg>
                                        Копировать
                                    </>
                                )}
                            </button>

                            <a
                                href={getSmsHref()}
                                className="sms-action-btn sms-action-primary"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                </svg>
                                Открыть SMS
                            </a>
                        </div>

                        {/* Status / Polling */}
                        {status === "pending" && (
                            <div className="sms-polling-status">
                                <div className="sms-spinner"></div>
                                <p className="sms-polling-text">{statusMessage}</p>
                                <p className="sms-polling-hint">
                                    Ожидаем SMS-подтверждение…
                                </p>
                            </div>
                        )}

                        {status === "success" && (
                            <div className="auth-info" role="status">
                                <div className="auth-info-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                                <p className="auth-info-text">{statusMessage}</p>
                            </div>
                        )}

                        {status === "error" && (
                            <div className="auth-error" role="alert">
                                <div className="auth-error-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                    </svg>
                                </div>
                                <div className="auth-error-content">
                                    <p className="auth-error-text">{errorMessage}</p>
                                </div>
                            </div>
                        )}

                        <div className="sms-verify-actions">
                            <button
                                type="button"
                                className="auth-back-btn"
                                onClick={handleBack}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m15 18-6-6 6-6"></path>
                                </svg>
                                {flow === "change" ? "Вернуться в профиль" : "Изменить номер"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
